import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { loginUserWithGoogleToken } from "Features/Auth/apis/registerUser";
import { useLoginWithGoogle } from "Features/Auth/hooks/useGoogleLogin";
import { useUserInfo } from "GlobalStates/User";
import { ROUTES } from "Features/Routes/routeConstants";
import ReactGA from "react-ga4";
import { getConfig } from "Config/config";

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser } = useUserInfo();
  const googleOAuthId = getConfig().GoogleOAuthID;
  const canAutoStartGoogleLogin =
    Boolean(googleOAuthId) && googleOAuthId !== "local-preview";

  const loginUserMutation = useMutation({
    mutationFn: loginUserWithGoogleToken,
    onSuccess: (data) => {
      ReactGA.event("login", { method: "google" });

      setUser(() => ({
        token: data?.data?.accessToken,
        tier: data?.data?.tier,
        completedCollegeFinder: data?.data?.completedCollegeFinder,
        name: data?.data?.name,
        picture: data?.data?.picture,
        id: data?.data?.id,
        email: data?.data?.email,
      }));

      // Always go to dashboard after login
      navigate(ROUTES.DASHBOARD);
    },
  });

  const generateGoogleToken = useLoginWithGoogle({
    onSuccessCallback: (user) => {
      loginUserMutation.mutate({ token: user?.access_token });
    },
  });

  // Trigger Google SSO automatically on mount
  useEffect(() => {
    if (!canAutoStartGoogleLogin) {
      return;
    }

    // Small timeout gives the page time to paint first
    // Some browsers allow popups slightly after mount
    const t = setTimeout(() => {
      try {
        generateGoogleToken();
      } catch {
        // Blocked — user will see the fallback button
      }
    }, 300);
    return () => clearTimeout(t);
  }, [canAutoStartGoogleLogin, generateGoogleToken]);

  return (
    <div
      style={{
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        padding: "clamp(3rem, 10vh, 7rem) 1.5rem 3rem",
        fontFamily: "sans-serif",
        color: "#101828",
      }}
    >
      <div
        style={{
          width: "min(100%, 420px)",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0.85rem",
          textAlign: "center",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: "0.75rem",
            height: "0.75rem",
            borderRadius: "999px",
            background: "#80e280",
            boxShadow: "0 0 0 0.5rem rgba(128, 226, 128, 0.18)",
            marginBottom: "0.5rem",
          }}
        />
        <h1
          style={{
            margin: 0,
            fontSize: "clamp(1.75rem, 2.5vw, 2.25rem)",
            lineHeight: 1.15,
            fontWeight: 800,
          }}
        >
          Signing you in
        </h1>
        <p
          style={{
            margin: 0,
            color: "#475467",
            fontSize: "1rem",
            lineHeight: 1.55,
          }}
        >
          We are opening Google to complete your login.
        </p>
        <p
          style={{
            margin: "0 0 0.25rem",
            color: "#667085",
            fontSize: "0.95rem",
            lineHeight: 1.5,
          }}
        >
          If the Google window did not open, continue below.
        </p>
        {/* Fallback button in case browser blocks the auto popup */}
        <button
          onClick={() => generateGoogleToken()}
          style={{
            minWidth: "16rem",
            minHeight: "3rem",
            background: "#80e280",
            border: "none",
            borderRadius: "2rem",
            padding: "0.75rem 2rem",
            color: "#101828",
            fontWeight: 800,
            fontSize: "1rem",
            cursor: "pointer",
          }}
        >
          Continue with Google
        </button>
      </div>
    </div>
  );
}
